// own-tracks.js
// Verwerkt OWN tracks: CSV importeren, proxy URL bouwen, localStorage opslaan/laden

const PROXY_BASE = 'https://lingering-frog-566e.makingmoneywithyourhobby.workers.dev/?url=';
const STORAGE_KEY = 'own-tracks';

function sanitize(str) {
    return str
        .trim()
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/\//g, '&#x2F;');
}

export function proxifyImage(url) {
    if (!url) return url;
    if (url.startsWith('https://lingering-frog-566e.makingmoneywithyourhobby.workers.dev')) return url;
    return `https://lingering-frog-566e.makingmoneywithyourhobby.workers.dev/proxy/image?url=${encodeURIComponent(url)}`;
}

export function parseOwnTracksCSV(csvText) {
    const lines = csvText.split('\n');
    const tracks = [];

    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line.startsWith(';')) continue;

        const cols = splitCSVLine(line);

        let src = cols[0]?.trim();
        const artist = cols[1] ? sanitize(cols[1]) : null;
        const title = cols[2] ? sanitize(cols[2]) : null;
        const album = cols[3] ? sanitize(cols[3]) : null;
        const coverUrl = cols[4]?.trim() || null;

        if (!src || !artist || !title) continue;

        // === CLEAN GBUCLOUD PARAMS before encoding ===
        if (src.includes('gbucloud.com')) {
            src = src
                .replace(/([?&])thumbnail=[^&]*/i, '')
                .replace(/([?&])workspaceId=[^&]*/i, '')
                .replace(/([?&])password=[^&]*/i, '')
                .replace(/[?&]$/, ''); // Remove trailing ? or &
        }

        const proxifiedCover = proxifyImage(coverUrl);

        tracks.push({
            id: 'own-' + (src.match(/file-entries\/(\d+)/)?.[1] ?? Math.random().toString(36).slice(2, 10)),
            title: title,
            artist: { name: artist },
            artists: [{ id: 0, name: artist }],
            album: {
                title: album,
                cover: proxifiedCover,
            },
            coverUrl: proxifiedCover,
            audioUrl: PROXY_BASE + encodeURIComponent(src),
            isLocal: true,
            isOwn: true,
            duration: null,
        });
    }

    return tracks;
}

function splitCSVLine(line) {
    const cols = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (ch === ';' && !inQuotes) {
            cols.push(current);
            current = '';
        } else {
            current += ch;
        }
    }
    cols.push(current);
    return cols;
}

// Leest WAV header (44 bytes) voor snelle duration berekening
async function getAudioDuration(url) {
    try {
        const res = await fetch(url, { headers: { Range: 'bytes=0-43' } });
        if (res.ok || res.status === 206) {
            const buffer = await res.arrayBuffer();
            const view = new DataView(buffer);
            // Check RIFF header
            if (view.byteLength >= 44 && view.getUint32(0, false) === 0x52494646) {
                const sampleRate = view.getUint32(24, true);
                const channels = view.getUint16(22, true);
                const bitsPerSample = view.getUint16(34, true);
                const dataSize = view.getUint32(40, true);
                if (sampleRate > 0 && channels > 0 && bitsPerSample > 0) {
                    const duration = dataSize / (sampleRate * channels * (bitsPerSample / 8));
                    return Math.round(duration);
                }
            }
        }
    } catch {}

    // Fallback naar audio element als WAV header niet werkt
    return new Promise((resolve) => {
        const audio = new Audio();
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => resolve(Math.round(audio.duration));
        audio.onerror = () => resolve(null);
        audio.src = url;
    });
}

// Alle tracks parallel verwerken voor snelheid
export async function enrichTracksWithDuration(tracks, onProgress) {
    let completed = 0;
    await Promise.all(
        tracks.map(async (track) => {
            try {
                const duration = await getAudioDuration(track.audioUrl);
                track.duration = duration;
            } catch {
                track.duration = null;
            }
            completed++;
            if (onProgress) onProgress(completed, tracks.length);
        })
    );
}

export function saveOwnTracks(tracks) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tracks));
}

export function loadOwnTracks() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

export function buildOwnPlaylist(tracks, name) {
    return {
        id: 'own-playlist',
        title: name || 'Own',
        tracks: tracks,
        isOwn: true,
    };
}

// ============================================================
// Fuzzy match helper voor zoekresultaten
// ============================================================
function normalizeForMatch(str) {
    return (str || '')
        .toLowerCase()
        .replace(/\(.*?\)/g, '')
        .replace(/[^a-z0-9]/g, '')
        .trim();
}

export function findOwnTrackMatch(tidalTrack) {
    const ownTracks = loadOwnTracks();
    const tidalTitle = normalizeForMatch(tidalTrack.title);
    const tidalArtist = normalizeForMatch(tidalTrack.artist?.name || tidalTrack.artists?.[0]?.name || '');

    return (
        ownTracks.find((t) => {
            const ownTitle = normalizeForMatch(t.title);
            const ownArtist = normalizeForMatch(t.artist);
            return ownTitle === tidalTitle && ownArtist === tidalArtist;
        }) || null
    );
}
